import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    FiUploadCloud, FiAlertTriangle, FiCheck, FiX, FiFileText,
    FiArrowLeft, FiPlus, FiCheckCircle, FiEdit2, FiLayers, FiTrash2,
} from "react-icons/fi";
import { triggerUpsell } from "../../utils/upsellBus";
import PageHeader from "../../components/common/pageheader/PageHeader";
import Loader from "../../components/common/Loader/Loader";
import QuestionContent from "../../components/common/QuestionContent/QuestionContent";
import QuestionFormModal from "../examBase/questionFormModal/QuestionFormModal";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import Select from "../../components/common/select/Select";
import { getSubjectsWithQuestionCount } from "../../services/subjectsApi";
import { createExamBase, assignQuestionsToExamBase } from "../../services/examBasesApi";
import {
    uploadAndRegister,
    getImportJob,
    getImportJobs,
    getImportedQuestions,
    updateImportedQuestion,
    setImportedQuestionStatus,
    setImportedQuestionsSubject,
    setCorrectOption,
    setQuestionImage,
    commitImport,
    deleteImportJob,
} from "../../services/questionImportApi";
import { formatDateTime } from "../../utils/formatDateTime";
import styles from "./QuestionImportPage.module.css";

/** Terminal job states — stop polling once we reach one of these. */
const DONE_STATES = ["READY_FOR_REVIEW", "COMMITTED", "FAILED"];
/** Still working — reopening one of these resumes the progress poll. */
const RUNNING_STATES = ["PENDING", "PROCESSING"];
/** Has staged questions worth loading. */
const REVIEWABLE_STATES = ["READY_FOR_REVIEW", "COMMITTED"];
/** Imports shown per page of "Əvvəlki idxallar". */
const JOBS_PAGE_SIZE = 10;
/** Staged questions shown per page inside one import. */
const QUESTIONS_PAGE_SIZE = 10;

const QuestionImportPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [subjects, setSubjects] = useState([]);
    const [subjectId, setSubjectId] = useState("");
    const [file, setFile] = useState(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [busy, setBusy] = useState(false);

    const [jobs, setJobs] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [jobsPage, setJobsPage] = useState(1);
    const [jobsTotalPages, setJobsTotalPages] = useState(1);
    const [job, setJob] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [qPage, setQPage] = useState(1);
    const [qTotalPages, setQTotalPages] = useState(1);
    /** Job-wide totals from the server — the filter chips and the "x of y" readout describe the
        whole import, so they must not be recomputed from the page currently loaded. */
    const [counts, setCounts] = useState({ all: 0, accepted: 0, rejected: 0, missingKey: 0 });
    const [committedIds, setCommittedIds] = useState([]);
    /** ALL | NO_KEY | ACCEPTED | REJECTED — triage for long imports. */
    const [filter, setFilter] = useState("ALL");
    /** The staged question currently open in the shared question editor. */
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [templateOpen, setTemplateOpen] = useState(false);
    const [template, setTemplate] = useState({ title: "", duration: 60, passRate: 50 });
    const [creatingTemplate, setCreatingTemplate] = useState(false);
    /** Rows checked for the bulk subject action. Kept across page turns within one job on purpose:
        check on page 1, page over, check more, apply once. */
    const [bulkSelected, setBulkSelected] = useState(new Set());
    const [bulkSubjectId, setBulkSubjectId] = useState("");
    const [bulkApplying, setBulkApplying] = useState(false);
    /** Import queued for deletion — drives the confirm modal. */
    const [jobToDelete, setJobToDelete] = useState(null);
    const [deletingJob, setDeletingJob] = useState(false);

    const pollRef = useRef(null);

    const label = (key, fallback) => t(key, { defaultValue: fallback });

    /**
     * "Subject › Topic" for the pickers. The selection DTO calls the topic name `title`
     * (SubjectSelectDTO: id, title, questionCount, parentId, parentName).
     */
    const subjectLabel = (s) => (s.parentName ? `${s.parentName} › ${s.title}` : s.title);

    const reloadSubjects = useCallback(async () => {
        try {
            const data = await getSubjectsWithQuestionCount();
            setSubjects(Array.isArray(data) ? data : []);
        } catch {
            /* keep the previous list rather than emptying the pickers */
        }
    }, []);

    /**
     * A topic created from inside the editor. TopicSelector already selects it, but it must be in
     * the list prop straight away or the picker's label blanks until the refetch lands — so add it
     * optimistically first, same as the question bank does.
     */
    const handleTopicCreated = useCallback((newTopic) => {
        if (newTopic?.id) {
            setSubjects((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.some((s) => Number(s.id) === Number(newTopic.id)) ? list : [...list, newTopic];
            });
        }
        reloadSubjects();
    }, [reloadSubjects]);

    /**
     * One page of staged questions, plus the job-wide counts. The counts come from the server
     * because they describe the whole import — deriving them from the ten rows on screen would
     * report the page as though it were the job.
     */
    const loadQuestions = useCallback(async (jobId, { filter: f = "ALL", pageNo = 1 } = {}) => {
        setLoadingQuestions(true);
        try {
            const data = await getImportedQuestions(jobId, { filter: f, pageNo, pageSize: QUESTIONS_PAGE_SIZE });
            setQuestions(Array.isArray(data?.content) ? data.content : []);
            setQTotalPages(data?.totalPages || 1);
            setCounts({
                all: data?.allCount || 0,
                accepted: data?.acceptedCount || 0,
                rejected: data?.rejectedCount || 0,
                missingKey: data?.missingKeyCount || 0,
            });
            setCommittedIds(Array.isArray(data?.committedQuestionIds) ? data.committedQuestionIds : []);
        } catch {
            toast.error(t("pages.questionImport.loadError", { defaultValue: "Suallar yüklənmədi" }));
        } finally {
            setLoadingQuestions(false);
        }
    }, [t]);

    const startPolling = useCallback((jobId) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const fresh = await getImportJob(jobId);
                setJob(fresh);
                if (DONE_STATES.includes(fresh.status)) {
                    clearInterval(pollRef.current);
                    // No fetch here: setJob above changes job.status, which is what the loader
                    // effect keys on. Calling it too would just double the request.
                }
            } catch {
                clearInterval(pollRef.current);
            }
        }, 4000);
    }, []);

    /**
     * Navigate to one import. Deliberately a PUSH, not a replace: the detail view is a place the
     * user navigated to, so Back has to come out of it. The actual opening happens in the effect
     * below — this only moves the URL, which is what makes the in-page button and the browser's
     * own Back button take exactly the same path.
     */
    const selectJob = useCallback((target) => {
        setSearchParams({ job: String(target.id) });
    }, [setSearchParams]);

    const backToList = useCallback(() => {
        setSearchParams({});
    }, [setSearchParams]);

    const selectedJobId = searchParams.get("job");

    /** Load a tenant's imports for the list page. */
    const loadJobs = useCallback(async (page) => {
        setLoadingJobs(true);
        try {
            const data = await getImportJobs({ pageNo: page, pageSize: JOBS_PAGE_SIZE });
            setJobs(Array.isArray(data?.content) ? data.content : []);
            setJobsTotalPages(data?.totalPages || 1);
        } catch {
            setJobs([]);
        } finally {
            setLoadingJobs(false);
        }
    }, []);

    useEffect(() => { reloadSubjects(); }, [reloadSubjects]);

    // The list is only mounted when no job is open, so don't fetch it behind the detail view.
    // Clearing the flag on the other branch matters: loadingJobs starts true and drives a fullscreen
    // loader, so opening ?job=123 directly would otherwise spin forever over the job we did load.
    useEffect(() => {
        if (selectedJobId) setLoadingJobs(false);
        else loadJobs(jobsPage);
    }, [selectedJobId, jobsPage, loadJobs]);

    /**
     * The open job is derived from the URL rather than held as independent state. That is what makes
     * browser Back (and a touchpad swipe, which is the same popstate) return to the list instead of
     * leaving the detail on screen with a stale URL — and it means a ?job= link can be shared.
     */
    useEffect(() => {
        clearInterval(pollRef.current);
        setEditingQuestion(null);

        if (!selectedJobId) {
            setJob(null);
            setQuestions([]);
            return;
        }
        if (job && String(job.id) === selectedJobId) return;   // already showing this one

        let alive = true;
        setQuestions([]);
        setFilter("ALL");
        setQPage(1);
        setBulkSelected(new Set());
        setBulkSubjectId("");
        (async () => {
            try {
                const fresh = await getImportJob(selectedJobId);
                if (!alive) return;
                setJob(fresh);
                // Questions are NOT fetched here — the effect below owns that, keyed on
                // (job, filter, page), so there is exactly one path that loads them.
                if (RUNNING_STATES.includes(fresh.status)) {
                    startPolling(fresh.id);
                }
            } catch {
                // Deleted, or someone else's job — fall back to the list rather than a blank screen.
                if (alive) setSearchParams({}, { replace: true });
            }
        })();
        return () => { alive = false; };
        // `job` is read only to skip redundant reloads; including it would re-run on every poll tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJobId]);

    // The one place staged questions are fetched. Keyed on the job plus the active filter and page,
    // so changing either simply re-runs it — no call sites to keep in sync.
    useEffect(() => {
        if (job?.id && REVIEWABLE_STATES.includes(job.status)) {
            loadQuestions(job.id, { filter, pageNo: qPage });
        }
        // job.status is read, not depended on for identity — polling replaces `job` every tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.id, job?.status, filter, qPage]);

    useEffect(() => () => clearInterval(pollRef.current), []);

    // ── upload ────────────────────────────────────────────────────────────────
    const handleUpload = async () => {
        if (!file) return;
        setBusy(true);
        setUploadPct(0);
        try {
            const created = await uploadAndRegister(
                { file, subjectId: subjectId || null, language: i18n.language || "az" },
                setUploadPct
            );
            setJobs((prev) => [created, ...prev]);
            setFile(null);
            selectJob(created);
        } catch (e) {
            // A page-quota rejection already opened the upsell modal from the axios layer; a toast on
            // top of it would just talk over the thing offering the fix. Keep the chosen file so the
            // user can retry straight after buying.
            if (!e?.isSubscriptionLimit) {
                toast.error(e?.response?.data?.message || label("pages.questionImport.uploadError", "Yükləmə alınmadı"));
            }
        } finally {
            setBusy(false);
        }
    };

    // ── review ────────────────────────────────────────────────────────────────
    const changeStatus = async (ids, status) => {
        if (!ids.length) return;
        try {
            await setImportedQuestionStatus(ids, status);
            setQuestions((prev) => prev.map((q) => (ids.includes(q.id) ? { ...q, status } : q)));
            // Then resync: the change may move rows out of the active filter and always moves the
            // job-wide counts, and neither is derivable from the page we hold.
            loadQuestions(job.id, { filter, pageNo: qPage });
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.statusError", "Status dəyişmədi"));
        }
    };

    // Job-wide, straight from the server. Only `editable`/`allSelected` are page-scoped, because
    // "select all" can only act on the rows actually loaded.
    const acceptedCount = counts.accepted;
    const rejectedCount = counts.rejected;
    const missingKeyCount = counts.missingKey;
    const editable = questions.filter((q) => q.status !== "COMMITTED");
    const allSelected = editable.length > 0 && editable.every((q) => q.status === "ACCEPTED");

    const toggleSelectAll = () =>
        changeStatus(editable.map((q) => q.id), allSelected ? "PENDING" : "ACCEPTED");

    // ── bulk subject assignment ───────────────────────────────────────────────
    const toggleBulkOne = (id) =>
        setBulkSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    // The master checkbox covers the LOADED page only — other pages' ids aren't on the client.
    // "Apply to all" below covers the whole import server-side instead.
    const pageAllPicked = editable.length > 0 && editable.every((q) => bulkSelected.has(q.id));
    const toggleBulkPage = () =>
        setBulkSelected((prev) => {
            const next = new Set(prev);
            editable.forEach((q) => (pageAllPicked ? next.delete(q.id) : next.add(q.id)));
            return next;
        });

    const applyBulkSubject = async (wholeJob) => {
        if (!bulkSubjectId || bulkApplying) return;
        setBulkApplying(true);
        try {
            const res = await setImportedQuestionsSubject(
                wholeJob
                    ? { jobId: job.id, subjectId: bulkSubjectId }
                    : { questionIds: [...bulkSelected], subjectId: bulkSubjectId }
            );
            toast.success(res?.message || label("pages.questionImport.subjectApplied", "Mövzu təyin edildi"));
            setBulkSelected(new Set());
            loadQuestions(job.id, { filter, pageNo: qPage });
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.subjectApplyError", "Mövzu təyin edilmədi"));
        } finally {
            setBulkApplying(false);
        }
    };

    // ── job deletion ──────────────────────────────────────────────────────────
    const confirmDeleteJob = async () => {
        if (!jobToDelete || deletingJob) return;
        setDeletingJob(true);
        try {
            await deleteImportJob(jobToDelete.id);
            toast.success(label("pages.questionImport.jobDeleted", "İdxal silindi"));
            setJobToDelete(null);
            // Deleting the page's last row must not strand the user on an empty page.
            if (jobs.length === 1 && jobsPage > 1) setJobsPage((p) => p - 1);
            else loadJobs(jobsPage);
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.jobDeleteError", "Silinmədi"));
        } finally {
            setDeletingJob(false);
        }
    };

    // Filtering is done in the database now — a filter applied to the ten rows on screen
    // would search the page instead of the import.
    const visibleQuestions = questions;

    /** Set the answer key straight from the list; clicking the current answer clears it. */
    const chooseAnswer = async (q, optionId) => {
        const current = (q.options || []).find((o) => o.correct);
        try {
            const saved = await setCorrectOption(q.id, current && current.id === optionId ? null : optionId);
            setQuestions((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
        loadQuestions(job.id, { filter, pageNo: qPage });
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.saveError", "Yadda saxlanmadı"));
        }
    };

    /**
     * Persistence override for the shared question editor. The form, validation, image handling and
     * inline topic creation are all the modal's; only the write target differs, because these
     * questions live in the import staging table until the job is committed.
     */
    const persistStaged = async ({ payload, file: mainFile, optionFiles }) => {
        let saved = await updateImportedQuestion(editingQuestion.id, {
            text: payload.text,
            type: payload.type,
            difficultyLevel: payload.difficultyLevel,
            correctAnswer: payload.correctAnswer,
            explanation: payload.explanation,
            subjectId: payload.subjectId || null,
            options: (payload.options || []).map((o) => ({
                id: o.id ?? null,
                label: o.label ?? null,
                optionText: o.optionText,
                correct: !!o.correct,
                removeAttachment: !!o.removeAttachment,
            })),
        });

        if (mainFile) {
            saved = await setQuestionImage(saved.id, mainFile, null);
        }
        // optionFiles is keyed by the option's index, which lines up with the saved order.
        for (const [index, optionFile] of Object.entries(optionFiles || {})) {
            const target = (saved.options || [])[Number(index)];
            if (target && optionFile) {
                saved = await setQuestionImage(saved.id, optionFile, target.id);
            }
        }

        setQuestions((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
        loadQuestions(job.id, { filter, pageNo: qPage });
        setEditingQuestion(null);
    };

    const handleCommit = async () => {
        try {
            const res = await commitImport(job.id);
            toast.success(res?.message || label("pages.questionImport.committed", "Suallar banka əlavə edildi"));
            const fresh = await getImportJob(job.id);
            setJob(fresh);
            setJobs((prev) => prev.map((j) => (j.id === fresh.id ? fresh : j)));
            loadQuestions(job.id, { filter, pageNo: qPage });
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.commitError", "Əlavə edilmədi"));
        }
    };

    /** Build an exam template straight from the questions this import put into the bank. */
    const handleCreateTemplate = async () => {
        if (!template.title.trim() || !committedIds.length) return;
        setCreatingTemplate(true);
        try {
            // createExamBase maps durationMinutes/passRate onto the backend's default* fields itself.
            const created = await createExamBase({
                title: template.title.trim(),
                durationMinutes: Number(template.duration),
                passRate: Number(template.passRate),
                subjectId: job?.subjectId || undefined,
            });
            // The controller returns a ResponseDTO and exposes the new id under `data.id`.
            const examBaseId = created?.data?.id;
            if (!examBaseId) {
                throw new Error("missing exam base id");
            }
            await assignQuestionsToExamBase(examBaseId, { questionIds: committedIds });
            toast.success(label("pages.questionImport.templateCreated", "İmtahan şablonu yaradıldı"));
            setTemplateOpen(false);
            navigate(examBaseId ? `/exam-bases/${examBaseId}/questions` : "/exams/base");
        } catch (e) {
            toast.error(e?.response?.data?.message || label("pages.questionImport.templateError", "Şablon yaradılmadı"));
        } finally {
            setCreatingTemplate(false);
        }
    };

    /**
     * Rendered both above and below the question list. Ten cards is a long scroll, and having to
     * come back to the top just to reach page 2 is the whole complaint — so the same control sits
     * at each end rather than a second, subtly different copy.
     */
    const questionPager = qTotalPages > 1 ? (
        <div className={styles.jobsPager}>
            <button type="button"
                    onClick={() => setQPage((p) => Math.max(1, p - 1))}
                    disabled={loadingQuestions || qPage <= 1}>
                {t("common.previous")}
            </button>
            <span className={styles.jobsPagerLabel}>
                {t("common.pageOf", { page: qPage, total: qTotalPages })}
            </span>
            <button type="button"
                    onClick={() => setQPage((p) => Math.min(qTotalPages, p + 1))}
                    disabled={loadingQuestions || qPage >= qTotalPages}>
                {t("common.next2")}
            </button>
        </div>
    ) : null;

    const progressPct = job?.totalPages
        ? Math.round(((job.processedPages || 0) / job.totalPages) * 100)
        : 0;
    const jobStatusLabel = (status) => t(`pages.questionImport.jobStatus.${status}`, { defaultValue: status });

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div>
            <PageHeader title={label("pages.questionImport.title", "Sənəddən sual idxalı")} />

            {loadingJobs && <Loader fullscreen />}

            {!loadingJobs && !job && (
                <>
                    <div className={styles.card}>
                        <div className={styles.uploadRow}>
                            <label className={styles.fileLabel}>
                                <FiUploadCloud />
                                <span>{file ? file.name : label("pages.questionImport.chooseFile", "PDF və ya Word seçin")}</span>
                                {/* Word documents are laid out to PDF on the server, so the same
                                    page-based extraction and quota apply either way. */}
                                <input type="file" hidden
                                       accept="application/pdf,.pdf,.docx,.doc,.rtf,.odt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                       onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            </label>

                            <Select
                                inline
                                className={styles.select}
                                value={subjectId}
                                onChange={(e) => setSubjectId(e.target.value)}
                                options={[
                                    { value: "", label: label("pages.questionImport.chooseSubject", "Mövzu seçin (istəyə bağlı)") },
                                    ...subjects.map((s) => ({ value: s.id, label: subjectLabel(s) })),
                                ]}
                            />

                            <button type="button" className={styles.primaryBtn} disabled={!file || busy}
                                    onClick={handleUpload}>
                                {busy ? `${uploadPct}%` : label("pages.questionImport.upload", "Yüklə və emal et")}
                            </button>
                        </div>

                        <p className={styles.hint}>
                            <FiAlertTriangle />{" "}
                            {label("pages.questionImport.keyHint",
                                "Diqqət: sistem PDF və ya Word sənədi üzərindəki əl yazısı işarələrini düzgün cavab kimi qəbul etmir — onlar tez-tez səhv olur. Düzgün cavabları yoxlama mərhələsində siz təyin edirsiniz.")}
                        </p>
                    </div>

                    <div className={styles.card}>
                        <h3 className={styles.jobTitle}>
                            <FiFileText /> {label("pages.questionImport.previousImports", "Əvvəlki idxallar")}
                        </h3>
                        {jobs.length === 0 && (
                            <p className={styles.progressText}>
                                {label("pages.questionImport.noJobs", "Hələ heç bir idxal yoxdur")}
                            </p>
                        )}
                        {jobs.length > 0 && (
                            <div className={`${styles.jobRow} ${styles.jobHead}`}>
                                <span>{label("pages.questionImport.colFile", "Fayl")}</span>
                                <span>{label("pages.questionImport.colStatus", "Status")}</span>
                                <span className={styles.numCell}>{label("pages.questionImport.colPages", "Səhifə")}</span>
                                <span className={styles.numCell}>{label("pages.questionImport.colExtracted", "Çıxarılıb")}</span>
                                <span className={styles.dateCell}>{label("pages.questionImport.uploadedAt", "Yükləndi")}</span>
                                <span className={styles.dateCell}>{label("pages.questionImport.committedAt", "Banka əlavə edildi")}</span>
                                <span aria-hidden />
                            </div>
                        )}
                        {jobs.map((j) => {
                            // COMMITTED is the bank's provenance record; PROCESSING has a live worker
                            // writing under it. Everything else may be deleted.
                            const deletable = j.status !== "COMMITTED" && j.status !== "PROCESSING";
                            return (
                                // A div acting as a button, because the delete control inside must be a
                                // real <button> and buttons cannot nest.
                                <div key={j.id} role="button" tabIndex={0}
                                     className={`${styles.jobRow} ${j.status === "COMMITTED" ? styles.jobRowDone : ""}`}
                                     onClick={() => selectJob(j)}
                                     onKeyDown={(e) => {
                                         if (e.key === "Enter" || e.key === " ") {
                                             e.preventDefault();
                                             selectJob(j);
                                         }
                                     }}>
                                    <span className={styles.jobMain}>
                                        {j.status === "COMMITTED" && <FiCheckCircle className={styles.jobDoneIcon} />}
                                        <span className={styles.jobName}>{j.originalFilename}</span>
                                    </span>
                                    <span>
                                        <span className={`${styles.jobBadge} ${styles[`job${j.status}`] || ""}`}>
                                            {jobStatusLabel(j.status)}
                                        </span>
                                    </span>
                                    {/* Pages billed against PDF_PAGE_LIMIT. Shown while PROCESSING as
                                        done/total, since that is exactly the quota being spent. */}
                                    <span className={styles.numCell}>
                                        {j.totalPages
                                            ? (j.status === "PROCESSING" && j.processedPages != null
                                                ? `${j.processedPages}/${j.totalPages}`
                                                : j.totalPages)
                                            : "—"}
                                    </span>
                                    <span className={styles.numCell}>{j.extractedCount || "—"}</span>
                                    <span className={`${styles.jobDate} ${styles.dateCell}`}>{formatDateTime(j.createdDateTime)}</span>
                                    <span className={`${j.status === "COMMITTED" ? styles.jobDone : styles.jobDate} ${styles.dateCell}`}>
                                        {j.status === "COMMITTED"
                                            ? `${j.committedAt ? formatDateTime(j.committedAt)
                                                : label("pages.questionImport.unknownDate", "tarix yoxdur")}${
                                                j.committedCount ? ` · ${j.committedCount}` : ""}`
                                            : "—"}
                                    </span>
                                    <span className={styles.jobActions}>
                                        {deletable && (
                                            <button type="button" className={styles.jobDeleteBtn}
                                                    title={label("pages.questionImport.deleteJob", "İdxalı sil")}
                                                    aria-label={label("pages.questionImport.deleteJob", "İdxalı sil")}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setJobToDelete(j);
                                                    }}>
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </span>
                                </div>
                            );
                        })}

                        {jobsTotalPages > 1 && (
                            <div className={styles.jobsPager}>
                                <button
                                    type="button"
                                    onClick={() => setJobsPage((p) => Math.max(1, p - 1))}
                                    disabled={loadingJobs || jobsPage <= 1}
                                >
                                    {t("common.previous")}
                                </button>
                                <span className={styles.jobsPagerLabel}>
                                    {t("common.pageOf", { page: jobsPage, total: jobsTotalPages })}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setJobsPage((p) => Math.min(jobsTotalPages, p + 1))}
                                    disabled={loadingJobs || jobsPage >= jobsTotalPages}
                                >
                                    {t("common.next2")}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {job && (
                <div className={styles.jobHeader}>
                    <button type="button" className={styles.linkBtn} onClick={backToList}>
                        <FiArrowLeft /> {label("pages.questionImport.backToList", "Siyahıya qayıt")}
                    </button>
                    <span className={styles.jobName}>{job.originalFilename}</span>
                    <button type="button" className={styles.secondaryBtn} onClick={backToList}>
                        <FiPlus /> {label("pages.questionImport.newImport", "Yeni idxal")}
                    </button>
                </div>
            )}

            {job && !DONE_STATES.includes(job.status) && (
                <div className={styles.card}>
                    {job.totalPages > 0 ? (
                        <>
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
                            </div>
                            <p className={styles.progressText}>
                                <span className={styles.spinner} />
                                {label("pages.questionImport.extracting", "Suallar çıxarılır")} —{" "}
                                <strong>{job.processedPages || 0}/{job.totalPages}</strong>{" "}
                                {label("pages.questionImport.pages", "səhifə")} ({progressPct}%) ·{" "}
                                {job.extractedCount || 0} {label("pages.questionImport.found", "sual tapıldı")}
                            </p>
                        </>
                    ) : (
                        <>
                            <div className={`${styles.progressBar} ${styles.indeterminate}`}>
                                <div className={styles.indeterminateFill} />
                            </div>
                            <p className={styles.progressText}>
                                <span className={styles.spinner} />
                                {job.status === "PENDING"
                                    ? label("pages.questionImport.queued", "Növbədədir — emal bir neçə saniyəyə başlayacaq")
                                    : label("pages.questionImport.preparing", "Sənəd açılır və səhifələr sayılır…")}
                            </p>
                        </>
                    )}
                    <p className={styles.progressHint}>
                        {label("pages.questionImport.keepOpenHint",
                            "Emal arxa planda davam edir — bu səhifədən çıxa bilərsiniz, nəticələr saxlanılacaq.")}
                    </p>
                </div>
            )}

            {job?.status === "FAILED" && (
                <div className={`${styles.card} ${styles.errorCard}`}>
                    <FiAlertTriangle /> {job.errorMessage || label("pages.questionImport.failed", "Emal alınmadı")}
                    {/* A job that died on quota is the one failure the user can actually fix, so give
                        them the same upsell modal the synchronous limit errors open. */}
                    {job.errorCode === "SUBSCRIPTION_LIMIT_REACHED" && (
                        <button
                            type="button"
                            className={styles.errorCardAction}
                            onClick={() =>
                                triggerUpsell({ message: job.errorMessage, limitCode: "PDF_PAGE_LIMIT" })
                            }
                        >
                            {label("pages.questionImport.buyPages", "Səhifə paketi al")}
                        </button>
                    )}
                </div>
            )}

            {job && REVIEWABLE_STATES.includes(job.status) && (
                <>
                    <div className={styles.reviewBar}>
                        <div className={styles.reviewTop}>
                            <label className={styles.selectAll}>
                                <input type="checkbox" checked={allSelected}
                                       ref={(el) => { if (el) el.indeterminate = acceptedCount > 0 && !allSelected; }}
                                       onChange={toggleSelectAll} disabled={!editable.length} />
                                <span>
                                    {allSelected
                                        ? label("pages.questionImport.deselectAll", "Seçimi ləğv et")
                                        : label("pages.questionImport.selectAll", "Hamısını seç")}
                                </span>
                            </label>

                            <span className={styles.counts}>
                                {t("pages.questionImport.selectedOf", {
                                    defaultValue: "{{total}} sualdan {{count}}-i seçilib",
                                    count: acceptedCount,
                                    total: questions.length,
                                })}
                            </span>

                            {committedIds.length > 0 && (
                                <button type="button" className={styles.secondaryBtn}
                                        onClick={() => {
                                            setTemplate((p) => ({ ...p, title: job.originalFilename?.replace(/\.pdf$/i, "") || "" }));
                                            setTemplateOpen(true);
                                        }}>
                                    <FiLayers /> {label("pages.questionImport.makeTemplate", "İmtahan şablonu yarat")}
                                </button>
                            )}

                            <button type="button" className={styles.primaryBtn}
                                    disabled={!acceptedCount || job.status === "COMMITTED"}
                                    onClick={handleCommit}>
                                {label("pages.questionImport.commit", "Banka əlavə et")} ({acceptedCount})
                            </button>
                        </div>

                        <div className={styles.filters}>
                            {[
                                ["ALL", label("pages.questionImport.filterAll", "Hamısı"), counts.all],
                                ["NO_KEY", label("pages.questionImport.filterNoKey", "Cavabı təyin edilməyib"), missingKeyCount],
                                ["ACCEPTED", label("pages.questionImport.filterAccepted", "Seçilmiş"), acceptedCount],
                                ["REJECTED", label("pages.questionImport.filterRejected", "İmtina edilmiş"), rejectedCount],
                            ].map(([key, text, count]) => (
                                <button key={key} type="button"
                                        className={`${styles.chip} ${filter === key ? styles.chipActive : ""}`}
                                        onClick={() => { setFilter(key); setQPage(1); }}>
                                    {text} <span className={styles.chipCount}>{count}</span>
                                </button>
                            ))}
                        </div>

                        {/* Bulk subject assignment. Hidden once the job is committed — every row is
                            frozen then and the backend would skip them all anyway. */}
                        {job.status !== "COMMITTED" && (
                            <div className={styles.bulkBar}>
                                <label className={styles.selectAll}>
                                    <input type="checkbox" checked={pageAllPicked}
                                           onChange={toggleBulkPage} disabled={!editable.length} />
                                    <span>{label("pages.questionImport.bulkPickPage", "Səhifədəkiləri seç")}</span>
                                </label>
                                <span className={styles.bulkCount}>
                                    {t("pages.questionImport.bulkSelectedCount", {
                                        defaultValue: "{{count}} sual seçilib",
                                        count: bulkSelected.size,
                                    })}
                                </span>
                                <Select
                                    inline
                                    className={styles.bulkSelect}
                                    value={bulkSubjectId}
                                    onChange={(e) => setBulkSubjectId(e.target.value)}
                                    options={[
                                        { value: "", label: label("pages.questionImport.bulkChooseSubject", "Mövzu seçin") },
                                        ...subjects.map((s) => ({ value: s.id, label: subjectLabel(s) })),
                                    ]}
                                />
                                <button type="button" className={styles.secondaryBtn}
                                        disabled={!bulkSelected.size || !bulkSubjectId || bulkApplying}
                                        onClick={() => applyBulkSubject(false)}>
                                    {label("pages.questionImport.applyToSelected", "Seçilmişlərə tətbiq et")} ({bulkSelected.size})
                                </button>
                                <button type="button" className={styles.secondaryBtn}
                                        disabled={!bulkSubjectId || bulkApplying || !counts.all}
                                        onClick={() => {
                                            // Job-wide overwrite of whatever topics were already set — the
                                            // one bulk action here worth a pause.
                                            if (window.confirm(t("pages.questionImport.applyToAllConfirm", {
                                                defaultValue: "Mövzu bu idxalın BÜTÜN suallarına ({{count}}) təyin ediləcək. Davam edilsin?",
                                                count: counts.all,
                                            }))) {
                                                applyBulkSubject(true);
                                            }
                                        }}>
                                    {label("pages.questionImport.applyToAll", "Hamısına tətbiq et")} ({counts.all})
                                </button>
                            </div>
                        )}
                    </div>

                    {/* The model is never asked to judge difficulty, so every imported question arrives
                        Medium. Saying so here — where the teacher is already editing questions — stops
                        it reading as a wrong guess by the AI. */}
                    <p className={styles.note}>
                        {label("pages.questionImport.difficultyNote",
                            "Qeyd: idxal olunan suallara standart olaraq «Orta» çətinlik təyin edilir. Hər sualı redaktə edərək dəyişə bilərsiniz.")}
                    </p>

                    {loadingQuestions && <Loader />}

                    {!loadingQuestions && questionPager}

                    {!loadingQuestions && visibleQuestions.length === 0 && (
                        <div className={styles.emptyFilter}>
                            {label("pages.questionImport.noneInFilter", "Bu filtrə uyğun sual yoxdur")}
                        </div>
                    )}

                    {!loadingQuestions && visibleQuestions.map((q) => (
                        <div key={q.id}
                             className={`${styles.qCard} ${q.status === "ACCEPTED" ? styles.qAccepted : ""} ${q.status === "REJECTED" ? styles.qRejected : ""}`}>
                            <div className={styles.qHead}>
                                {q.status !== "COMMITTED" && (
                                    <input type="checkbox" className={styles.qPick}
                                           checked={bulkSelected.has(q.id)}
                                           onChange={() => toggleBulkOne(q.id)}
                                           aria-label={label("pages.questionImport.pickForBulk", "Toplu mövzu təyini üçün seç")} />
                                )}
                                <span className={styles.qMeta}>
                                    {label("pages.questionImport.page", "Səh.")} {q.pageNumber}
                                    {q.sourceNumber ? ` · #${q.sourceNumber}` : ""}
                                    {q.confidence != null && (
                                        <span className={q.confidence < 0.7 ? styles.lowConfidence : undefined}
                                              title={label("pages.questionImport.confidenceHelp",
                                                  "Mətnin sənəddən nə qədər dəqiq oxunduğunu göstərir. Cavabın düzgünlüyü ilə əlaqəsi yoxdur.")}>
                                            {` · ${label("pages.questionImport.readAccuracy", "oxunma")} ${Math.round(q.confidence * 100)}%`}
                                        </span>
                                    )}
                                    {q.sourceTopic ? ` · ${q.sourceTopic}` : ""}
                                    {q.subjectName ? ` · ${q.subjectName}` : ""}
                                </span>
                                <div className={styles.qActions}>
                                    {q.status !== "COMMITTED" && (
                                        <>
                                            <button type="button"
                                                    className={`${styles.toggleBtn} ${q.status === "ACCEPTED" ? styles.toggleOn : ""}`}
                                                    onClick={() => changeStatus([q.id], q.status === "ACCEPTED" ? "PENDING" : "ACCEPTED")}>
                                                <FiCheck /> {label("pages.questionImport.accept", "Seç")}
                                            </button>
                                            <button type="button"
                                                    className={`${styles.toggleBtn} ${q.status === "REJECTED" ? styles.toggleOff : ""}`}
                                                    onClick={() => changeStatus([q.id], q.status === "REJECTED" ? "PENDING" : "REJECTED")}>
                                                <FiX /> {label("pages.questionImport.reject", "İmtina")}
                                            </button>
                                            {/* Same editor as the question bank — one form, one
                                                inline subject/topic creator, no second copy. */}
                                            <button type="button" className={styles.toggleBtn}
                                                    onClick={() => setEditingQuestion(q)}>
                                                <FiEdit2 /> {label("common.edit", "Redaktə")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {(q.warnings || []).length > 0 && (
                                <div className={styles.warnings}>
                                    {q.warnings.map((w) => (
                                        <span key={w} className={styles.warnBadge}
                                              title={t(`pages.questionImport.warningHelp.${w}`, { defaultValue: "" })}>
                                            {label(`pages.questionImport.warning.${w}`, w)}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <QuestionContent text={q.text} />
                            {q.attachmentUrl && <img src={q.attachmentUrl} alt="" className={styles.figure} />}

                            {q.type === "OPEN" ? (
                                <p className={styles.refPreview}>
                                    {q.correctAnswer || label("pages.questionImport.noReferenceAnswer", "Nümunə cavab yoxdur")}
                                </p>
                            ) : (
                                <ul className={styles.optionList}>
                                    {(q.options || []).map((o) => (
                                        <li key={o.id}
                                            className={`${o.correct ? styles.optCorrect : ""} ${q.status !== "COMMITTED" ? styles.optClickable : ""}`}
                                            onClick={q.status === "COMMITTED" ? undefined : () => chooseAnswer(q, o.id)}
                                            role={q.status === "COMMITTED" ? undefined : "button"}
                                            tabIndex={q.status === "COMMITTED" ? undefined : 0}
                                            onKeyDown={(e) => {
                                                if (q.status !== "COMMITTED" && (e.key === "Enter" || e.key === " ")) {
                                                    e.preventDefault();
                                                    chooseAnswer(q, o.id);
                                                }
                                            }}
                                            title={q.status === "COMMITTED" ? undefined
                                                : label("pages.questionImport.clickToMark", "Düzgün cavab kimi işarələmək üçün klikləyin")}>
                                            {q.status !== "COMMITTED" && (
                                                <span className={`${styles.optRadio} ${o.correct ? styles.optRadioOn : ""}`} />
                                            )}
                                            <span className={styles.optLabel}>{o.label}</span>
                                            <QuestionContent text={o.optionText} />
                                            {o.attachmentUrl && <img src={o.attachmentUrl} alt="" className={styles.optFigure} />}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {q.explanation && (
                                <details className={styles.explanation}>
                                    <summary>{label("pages.questionImport.explanationLabel", "İzah")}</summary>
                                    <QuestionContent text={q.explanation} />
                                </details>
                            )}
                        </div>
                    ))}

                    {!loadingQuestions && visibleQuestions.length > 0 && questionPager}
                </>
            )}

            {/* The question bank's own editor, pointed at the staging table. */}
            <QuestionFormModal
                isOpen={!!editingQuestion}
                onClose={() => setEditingQuestion(null)}
                mode="edit"
                initialQuestion={editingQuestion}
                subjectOptions={subjects}
                onSubjectsChanged={handleTopicCreated}
                onPersist={persistStaged}
                allowTopicChange
            />

            {templateOpen && (
                <div className={styles.modalBackdrop} onClick={() => setTemplateOpen(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>
                            {label("pages.questionImport.makeTemplate", "İmtahan şablonu yarat")}
                        </h3>
                        <p className={styles.modalHint}>
                            {t("pages.questionImport.templateHint", {
                                defaultValue: "Bu idxaldan banka əlavə edilmiş {{count}} sual şablona daxil ediləcək.",
                                count: committedIds.length,
                            })}
                        </p>

                        <label className={styles.modalLabel}>
                            {label("pages.questionImport.templateName", "Şablonun adı")}
                            <input className={styles.modalInput} value={template.title} maxLength={100}
                                   onChange={(e) => setTemplate({ ...template, title: e.target.value })} />
                        </label>

                        <div className={styles.modalRow}>
                            <label className={styles.modalLabel}>
                                {label("pages.questionImport.templateDuration", "Müddət (dəq)")}
                                <input type="number" min="1" className={styles.modalInput} value={template.duration}
                                       onChange={(e) => setTemplate({ ...template, duration: e.target.value })} />
                            </label>
                            <label className={styles.modalLabel}>
                                {label("pages.questionImport.templatePassRate", "Keçid faizi")}
                                <input type="number" min="1" max="100" className={styles.modalInput} value={template.passRate}
                                       onChange={(e) => setTemplate({ ...template, passRate: e.target.value })} />
                            </label>
                        </div>

                        <div className={styles.modalActions}>
                            <button type="button" className={styles.secondaryBtn} onClick={() => setTemplateOpen(false)}>
                                {label("common.cancel", "Ləğv et")}
                            </button>
                            <button type="button" className={styles.primaryBtn}
                                    disabled={!template.title.trim() || creatingTemplate}
                                    onClick={handleCreateTemplate}>
                                {label("common.create", "Yarat")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDeleteModal
                isOpen={!!jobToDelete}
                onCancel={() => setJobToDelete(null)}
                onConfirm={confirmDeleteJob}
                disabled={deletingJob}
                message={t("pages.questionImport.deleteConfirm", {
                    defaultValue: "\"{{name}}\" idxalı bütün çıxarılmış sualları ilə birlikdə silinsin? Bu əməliyyat geri qaytarıla bilməz.",
                    name: jobToDelete?.originalFilename || "",
                })}
            />
        </div>
    );
};

export default QuestionImportPage;
